/**
 * useHistogram React hook - Calculate histogram from rendered image
 * Feature: 012-histogram-display
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Application } from 'pixi.js';
import { HistogramData } from '../../types/histogram';
import { calculateHistogram, validateHistogramData } from './histogramUtils';
import logger from '../../utils/logger';

export interface UseHistogramOptions {
  /**
   * PixiJS application instance
   */
  app: Application | null;

  /**
   * Image bounds for pixel extraction (excludes canvas background)
   */
  imageBounds?: { x: number; y: number; width: number; height: number } | null;

  /**
   * Debounce delay in milliseconds (default: 50ms)
   * Reduces calculation frequency during rapid slider adjustments
   */
  debounceMs?: number;

  /**
   * Enable histogram calculation (default: true)
   */
  enabled?: boolean;
}

export interface UseHistogramResult {
  /**
   * Current histogram data (null if not yet calculated)
   */
  histogramData: HistogramData | null;

  /**
   * Whether histogram calculation is in progress
   */
  isCalculating: boolean;

  /**
   * Error that occurred during calculation (null if no error)
   */
  error: Error | null;

  /**
   * Manually trigger histogram recalculation
   */
  recalculate: () => void;
}

/**
 * Hook to calculate histogram from PixiJS rendered image
 *
 * @param options - Hook configuration
 * @returns Histogram data and calculation state
 *
 * @example
 * const { histogramData, isCalculating, error } = useHistogram({
 *   app: pixiApp,
 *   debounceMs: 50
 * });
 */
export function useHistogram(options: UseHistogramOptions): UseHistogramResult {
  const { app, imageBounds, debounceMs = 50, enabled = true } = options;

  const [histogramData, setHistogramData] = useState<HistogramData | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const debounceTimerRef = useRef<number | null>(null);
  const calculationCounterRef = useRef(0);

  /**
   * Calculate histogram from current PixiJS canvas state
   */
  const calculate = useCallback(() => {
    logger.debug('Histogram', 'Starting calculation, app:', !!app, 'enabled:', enabled);

    if (!app || !enabled) {
      logger.debug('Histogram', 'Skipping calculation - no app or disabled');
      return;
    }

    setIsCalculating(true);
    setError(null);

    // Increment counter to track this calculation attempt
    const currentCalculation = ++calculationCounterRef.current;

    try {
      // Extract pixel data from PixiJS canvas
      // Note: This triggers a GPU→CPU transfer (expensive operation)

      // Get the canvas from PixiJS
      const canvas = app.canvas as HTMLCanvasElement;
      if (!canvas) {
        throw new Error('Canvas not available from PixiJS application');
      }
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      logger.debug('Histogram', 'Canvas dimensions:', canvasWidth, 'x', canvasHeight);

      // If no image bounds provided, fall back to full canvas (legacy behavior)
      const bounds = imageBounds || { x: 0, y: 0, width: canvasWidth, height: canvasHeight };

      logger.debug('Histogram', 'Image bounds:', bounds);

      // Create a temporary 2D canvas to extract pixels from the WebGL canvas
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvasWidth;
      tempCanvas.height = canvasHeight;
      const ctx = tempCanvas.getContext('2d', { willReadFrequently: true });

      if (!ctx) {
        throw new Error('Failed to create 2D context for pixel extraction');
      }

      // Draw the WebGL canvas onto the 2D canvas
      // This allows us to extract pixels from the WebGL rendering
      ctx.drawImage(canvas, 0, 0);

      // Extract pixel data ONLY from the image bounds (excludes canvas background)
      const imageData = ctx.getImageData(bounds.x, bounds.y, bounds.width, bounds.height);

      logger.debug('Histogram', 'Extracted imageData:', imageData.width, 'x', imageData.height, 'data length:', imageData.data.length);

      // Use requestAnimationFrame to avoid blocking the main thread
      requestAnimationFrame(() => {
        try {
          // Only proceed if this is still the latest calculation request
          if (currentCalculation !== calculationCounterRef.current) {
            return; // Newer calculation was requested, skip this one
          }

          // Calculate histogram bins
          const startTime = performance.now();
          const histogram = calculateHistogram(imageData);
          const elapsed = performance.now() - startTime;

          // Helper function to find non-zero bins for better debugging
          const findNonZeroBins = (channel: Uint8Array) => {
            const nonZero = [];
            for (let i = 0; i < channel.length; i++) {
              if (channel[i] > 0) {
                nonZero.push({ bin: i, value: channel[i] });
              }
            }
            return nonZero;
          };

          logger.debug('Histogram', 'Calculated histogram:', {
            bucketInfo: {
              description: 'Histogram uses 256 bins (0-255) for 8-bit color values, normalized to 0-255 range',
              bins: 256,
              binIndex: 'Represents intensity value (0=black, 128=mid-gray, 255=white)',
              binValue: 'Normalized count (0-255, where 255 = bin with most pixels)',
              normalization: `Raw pixel count scaled by max bin value (${histogram.maxBinValue})`,
            },
            summary: {
              totalPixels: histogram.totalPixels,
              maxBinValue: histogram.maxBinValue,
              maxBinValueExplanation: 'Raw pixel count before normalization (highest bin across all channels)',
              elapsed: `${elapsed.toFixed(2)}ms`,
            },
            channels: {
              red: {
                nonZeroBins: findNonZeroBins(histogram.red).slice(0, 20),
                firstTenBins: Array.from(histogram.red.slice(0, 10)),
                lastTenBins: Array.from(histogram.red.slice(246, 256)),
              },
              green: {
                nonZeroBins: findNonZeroBins(histogram.green).slice(0, 20),
                firstTenBins: Array.from(histogram.green.slice(0, 10)),
                lastTenBins: Array.from(histogram.green.slice(246, 256)),
              },
              blue: {
                nonZeroBins: findNonZeroBins(histogram.blue).slice(0, 20),
                firstTenBins: Array.from(histogram.blue.slice(0, 10)),
                lastTenBins: Array.from(histogram.blue.slice(246, 256)),
              },
              luminance: {
                nonZeroBins: findNonZeroBins(histogram.luminance).slice(0, 20),
                firstTenBins: Array.from(histogram.luminance.slice(0, 10)),
                lastTenBins: Array.from(histogram.luminance.slice(246, 256)),
              },
            },
          });

          // Log full arrays (can be expanded in console)
          logger.debug('Histogram', 'Full histogram arrays (NORMALIZED VALUES 0-255):', {
            explanation: 'Array index (0-255) = intensity value, Array value = normalized count (0-255)',
            example: `red[26] = ${histogram.red[26]} means "Normalized height for red intensity 26 (scaled from raw pixel count / ${histogram.maxBinValue} * 255)"`,
            red: Array.from(histogram.red),
            green: Array.from(histogram.green),
            blue: Array.from(histogram.blue),
            luminance: Array.from(histogram.luminance),
          });

          // Validate histogram data integrity (development only)
          if (import.meta.env.DEV) {
            validateHistogramData(histogram);
          }

          // Log performance warning if calculation exceeds target
          if (elapsed > 10) {
            logger.log(
              'Histogram',
              'warn',
              `Calculation took ${elapsed.toFixed(2)}ms (target: <10ms). ` +
              `Consider GPU-accelerated approach for ${imageData.width}×${imageData.height} images.`
            );
          }

          logger.debug('Histogram', 'Setting histogram data');
          setHistogramData(histogram);
          setIsCalculating(false);
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          logger.error('Histogram', 'Calculation failed:', error);
          setError(error);
          setIsCalculating(false);
        }
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Histogram', 'Failed to extract image data:', error);
      setError(error);
      setIsCalculating(false);
    }
  }, [app, enabled, imageBounds]);

  /**
   * Debounced recalculation function
   */
  const recalculate = useCallback(() => {
    // Clear existing debounce timer
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
    }

    // Schedule new calculation after debounce delay
    debounceTimerRef.current = window.setTimeout(() => {
      calculate();
      debounceTimerRef.current = null;
    }, debounceMs);
  }, [calculate, debounceMs]);

  /**
   * Calculate histogram when app becomes available or enabled state changes
   */
  useEffect(() => {
    if (app && enabled) {
      // Initial calculation (no debounce)
      calculate();
    }

    return () => {
      // Cleanup: clear debounce timer on unmount
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, [app, enabled, calculate]);

  return {
    histogramData,
    isCalculating,
    error,
    recalculate,
  };
}
